import React, { useEffect, useState } from 'react';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Plus, Users, ClipboardCheck, Calendar, Copy, Check, Activity, Wand2, Video, TrendingDown, ChevronRight, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export default function TeacherDashboard() {
  const { t, lang, user } = useApp();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [filters, setFilters] = useState({});
  const [exoStats, setExoStats] = useState(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [activeTab, setActiveTab] = useState('ai');

  useEffect(() => {
    api.get('/analytics/overview').then(r => setStats(r.data));
    api.get('/students').then(r => setStudents(r.data));
    api.get('/curriculum/filters').then(r => setFilters(r.data));
    api.get('/exercises/stats/teacher').then(r => setExoStats(r.data)).catch(() => {});
  }, []);

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="teacher-dashboard">
      <div className="mb-6">
        <h1 className="text-3xl font-black">{t('welcome')}, {user?.full_name}</h1>
        <p className="text-muted-foreground">{t('portal_teacher')}</p>
      </div>

      {/* Quick action bar — most-used teacher actions, always visible */}
      <Card className="mb-6 overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-emerald-50 to-amber-50" data-testid="quick-actions">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-primary/70 font-bold mb-3">{lang === 'ar' ? '⚡ إجراءات سريعة' : '⚡ Quick Actions'}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <QuickAction icon={UserPlus} label={lang === 'ar' ? 'إضافة طالب' : 'Add student'} sublabel={lang === 'ar' ? 'تسجيل ملف طالب جديد' : 'Register a new student'} onClick={() => { setActiveTab('students'); setShowAddStudent(true); }} color="from-emerald-600 to-emerald-500" testid="qa-add-student" />
            <QuickAction icon={ClipboardCheck} label={lang === 'ar' ? 'خطة فردية' : 'New IEP'} sublabel={lang === 'ar' ? 'إنشاء خطة لطالب' : 'Create a student IEP'} to="/student-plans" color="from-primary to-emerald-700" testid="qa-new-iep" />
            <QuickAction icon={Wand2} label={lang === 'ar' ? 'توليد تمرين AI' : 'AI Exercise'} sublabel={lang === 'ar' ? 'تمرين معدّل بالذكاء الاصطناعي' : 'AI-designed exercise'} to="/exercises/generate" color="from-indigo-600 to-violet-600" testid="qa-gen-exercise" />
            <QuickAction icon={Video} label={lang === 'ar' ? 'تحليل أداء' : 'Video review'} sublabel={lang === 'ar' ? 'تحليل فيديو الطالب' : 'AI video review'} to="/exercises/analyze" color="from-violet-600 to-rose-500" testid="qa-analyze" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label={t('total_students')} value={students.length} />
        <StatCard label={t('total_goals')} value={stats?.total_goals ?? '—'} />
        <StatCard label={t('total_ieps')} value={stats?.total_ieps ?? '—'} />
        <StatCard label={t('avg_score')} value={stats?.avg_assessment_score ?? '—'} suffix="%" />
      </div>

      {/* Smart Movement Library tile */}
      <Card className="mb-6 overflow-hidden border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-violet-50 to-white" data-testid="exo-tile">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 grid place-items-center text-white shrink-0"><Activity className="w-6 h-6" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-bold">{lang === 'ar' ? 'منصة همم' : 'Himam'}</div>
                <h2 className="font-black text-lg">{lang === 'ar' ? 'مكتبة التمارين الحركية الذكية' : 'Smart Movement Exercise Library'}</h2>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                  <span><b className="text-indigo-700 stat-number">{exoStats?.total_exercises ?? 0}</b> {lang === 'ar' ? 'تمرين' : 'exercises'}</span>
                  <span><b className="text-indigo-700 stat-number">{exoStats?.total_attempts ?? 0}</b> {lang === 'ar' ? 'تقييم أداء' : 'reviews'}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button asChild size="sm" variant="outline" data-testid="tile-library"><Link to="/exercises"><Activity className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'فتح المكتبة' : 'Open library'}</Link></Button>
              <Button asChild size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="tile-generate"><Link to="/exercises/generate"><Wand2 className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'توليد AI' : 'AI generate'}</Link></Button>
              <Button asChild size="sm" variant="outline" className="border-violet-300 text-violet-700" data-testid="tile-analyze"><Link to="/exercises/analyze"><Video className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'تحليل فيديو' : 'Video review'}</Link></Button>
            </div>
          </div>

          {/* Weak skills + recent */}
          {exoStats && (exoStats.weak_skills?.length > 0 || exoStats.recent_attempts?.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-indigo-200">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-rose-600 font-bold mb-1.5 flex items-center gap-1"><TrendingDown className="w-3 h-3" />{lang === 'ar' ? 'مهارات تحتاج دعماً' : 'Skills needing support'}</div>
                {(exoStats.weak_skills || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">{lang === 'ar' ? '—' : '—'}</p>
                ) : (
                  <ul className="space-y-1" data-testid="weak-skills">
                    {exoStats.weak_skills.map(w => (
                      <li key={w.skill} className="flex items-center justify-between text-xs bg-white border border-border rounded p-2">
                        <span className="font-semibold">{w.skill}</span>
                        <Badge variant="secondary" className="text-[10px]">{w.avg_score}% · {w.attempts}x</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-violet-700 font-bold mb-1.5">{lang === 'ar' ? 'آخر التحليلات' : 'Recent reviews'}</div>
                {(exoStats.recent_attempts || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">{lang === 'ar' ? 'لا توجد بعد. ابدأ بتحليل فيديو.' : 'None yet. Start a review.'}</p>
                ) : (
                  <ul className="space-y-1" data-testid="recent-attempts">
                    {exoStats.recent_attempts.slice(0, 4).map(a => (
                      <li key={a.id} className="flex items-center justify-between text-xs bg-white border border-border rounded p-2">
                        <span className="font-semibold line-clamp-1 flex-1">{a.exercise_name_ar}</span>
                        <Badge className={`text-[10px] ${(a.score || 0) >= 80 ? 'bg-emerald-600' : (a.score || 0) >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`}>{a.score}%</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="ai" data-testid="tab-ai"><Sparkles className="w-4 h-4 me-2" />{t('ai_generator')}</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students"><Users className="w-4 h-4 me-2" />{t('total_students')}</TabsTrigger>
          <TabsTrigger value="iep" data-testid="tab-iep"><ClipboardCheck className="w-4 h-4 me-2" />{t('iep_builder')}</TabsTrigger>
          <TabsTrigger value="plan" data-testid="tab-plan"><Calendar className="w-4 h-4 me-2" />{t('overview')}</TabsTrigger>
        </TabsList>

        <TabsContent value="ai"><AIGenerator filters={filters} /></TabsContent>
        <TabsContent value="students"><StudentsPanel students={students} onCreate={(s) => setStudents([...students, s])} filters={filters} dialogOpen={showAddStudent} setDialogOpen={setShowAddStudent} /></TabsContent>
        <TabsContent value="iep"><IEPPanel students={students} /></TabsContent>
        <TabsContent value="plan"><PlanPanel /></TabsContent>
      </Tabs>
    </main>
  );
}

function QuickAction({ icon: Icon, label, sublabel, onClick, to, color = 'from-primary to-emerald-700', testid }) {
  const inner = (
    <div className="group bg-white border-2 border-border rounded-xl p-3 sm:p-4 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer h-full">
      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br ${color} grid place-items-center text-white shadow-md mb-2 group-hover:scale-110 transition-transform`}>
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>
      <div className="font-black text-sm leading-tight">{label}</div>
      <div className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">{sublabel}</div>
    </div>
  );
  if (to) return <Link to={to} data-testid={testid} className="block h-full">{inner}</Link>;
  return <button type="button" onClick={onClick} data-testid={testid} className="text-start w-full h-full">{inner}</button>;
}

function StatCard({ label, value, suffix }) {
  return (
    <Card className="card-elevate">
      <CardContent className="p-5">
        <div className="text-xs text-muted-foreground font-semibold mb-2">{label}</div>
        <div className="text-3xl font-black text-primary">{value}{suffix || ''}</div>
      </CardContent>
    </Card>
  );
}

function AIGenerator({ filters }) {
  const { t, lang } = useApp();
  const [form, setForm] = useState({
    disability_type: '', age_group: '', sport_activity: '', performance_level: 'متوسط', language: lang, extra_context: ''
  });
  const [output, setOutput] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!form.disability_type || !form.age_group || !form.sport_activity) {
      toast.error(lang === 'ar' ? 'يرجى تعبئة الحقول الأساسية' : 'Please fill key fields');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/ai/generate-lesson', { ...form, language: lang });
      setOutput(data.content);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('error'));
    } finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(output); setCopied(true); toast.success(t('copied')); setTimeout(() => setCopied(false), 1500); };

  return (
    <Card>
      <CardHeader><CardTitle>{t('ai_generator')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <FilterSelect label={t('filter_disability')} options={filters.disability_types} value={form.disability_type} onChange={(v) => setForm({ ...form, disability_type: v })} testid="ai-disability" />
          <FilterSelect label={t('filter_age')} options={filters.age_groups} value={form.age_group} onChange={(v) => setForm({ ...form, age_group: v })} testid="ai-age" />
          <FilterSelect label={t('filter_sport')} options={filters.sports} value={form.sport_activity} onChange={(v) => setForm({ ...form, sport_activity: v })} testid="ai-sport" />
          <FilterSelect label={t('filter_perf')} options={filters.performance_levels} value={form.performance_level} onChange={(v) => setForm({ ...form, performance_level: v })} testid="ai-perf" />
        </div>
        <div>
          <Label>{t('extra_context')}</Label>
          <Textarea value={form.extra_context} onChange={(e) => setForm({ ...form, extra_context: e.target.value })} rows={2} data-testid="ai-context" />
        </div>
        <Button onClick={generate} disabled={busy} className="bg-primary hover:bg-primary/90 h-11" data-testid="ai-generate-btn">
          <Sparkles className="w-4 h-4 me-2" /> {busy ? t('generating') : t('generate')}
        </Button>

        {output && (
          <div className="bg-secondary/50 rounded-lg p-4 mt-4" data-testid="ai-output">
            <div className="flex justify-end mb-2">
              <Button variant="outline" size="sm" onClick={copy} data-testid="ai-copy">
                {copied ? <Check className="w-4 h-4 me-1" /> : <Copy className="w-4 h-4 me-1" />}{copied ? t('copied') : t('copy')}
              </Button>
            </div>
            <div className="whitespace-pre-wrap text-sm leading-relaxed">{output}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FilterSelect({ label, options = [], value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="h-10"><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          {options.filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function StudentsPanel({ students, onCreate, filters, dialogOpen, setDialogOpen }) {
  const { t, lang } = useApp();
  const [internalOpen, setInternalOpen] = useState(false);
  // Use external state when provided (from Quick Action bar), else local
  const open = dialogOpen !== undefined ? dialogOpen : internalOpen;
  const setOpen = setDialogOpen || setInternalOpen;
  const emptyForm = { full_name_ar: '', full_name_en: '', age: 8, age_group: '', disability_type: '', disability_level: '', center: '', parent_email: '', student_email: '' };
  const [form, setForm] = useState(emptyForm);

  const submit = async () => {
    if (!form.full_name_ar.trim()) { toast.error(lang === 'ar' ? 'الاسم بالعربية مطلوب' : 'Arabic name required'); return; }
    // Light email validation — only check format if a value was entered.
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.parent_email && !emailRx.test(form.parent_email)) {
      toast.error(lang === 'ar' ? 'صيغة بريد ولي الأمر غير صحيحة' : 'Invalid parent email format');
      return;
    }
    if (form.student_email && !emailRx.test(form.student_email)) {
      toast.error(lang === 'ar' ? 'صيغة بريد الطالب غير صحيحة' : 'Invalid student email format');
      return;
    }
    try {
      const { data } = await api.post('/students', form);
      onCreate(data);
      toast.success(lang === 'ar' ? '✓ تم إضافة الطالب' : '✓ Student added');
      setOpen(false);
      setForm(emptyForm);
    } catch (e) { toast.error(e.response?.data?.detail || t('error')); }
  };

  return (
    <Card data-testid="students-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-primary" />{t('total_students')} <Badge variant="secondary" className="ms-1">{students.length}</Badge></CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-primary to-emerald-600 hover:opacity-90 shadow-md font-bold text-sm h-10 px-4" data-testid="add-student-btn">
              <UserPlus className="w-4 h-4 me-2" />{lang === 'ar' ? 'إضافة طالب جديد' : 'Add new student'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="add-student-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" />{lang === 'ar' ? 'طالب جديد' : 'New student'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">{lang === 'ar' ? 'الاسم بالعربية *' : 'Arabic name *'}</Label><Input value={form.full_name_ar} onChange={(e) => setForm({ ...form, full_name_ar: e.target.value })} data-testid="new-student-name" /></div>
                <div><Label className="text-xs">{lang === 'ar' ? 'الاسم بالإنجليزية' : 'English name'}</Label><Input value={form.full_name_en} onChange={(e) => setForm({ ...form, full_name_en: e.target.value })} data-testid="new-student-name-en" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">{lang === 'ar' ? 'العمر' : 'Age'}</Label><Input type="number" min={3} max={22} value={form.age} onChange={(e) => setForm({ ...form, age: Number(e.target.value) })} data-testid="new-student-age" /></div>
                <FilterSelect label={lang === 'ar' ? 'الفئة العمرية' : 'Age group'} options={filters.age_groups} value={form.age_group} onChange={(v) => setForm({ ...form, age_group: v })} testid="new-student-agegroup" />
              </div>
              <FilterSelect label={lang === 'ar' ? 'نوع الإعاقة' : 'Disability'} options={filters.disability_types} value={form.disability_type} onChange={(v) => setForm({ ...form, disability_type: v })} testid="new-student-disability" />
              <FilterSelect label={lang === 'ar' ? 'مستوى الإعاقة' : 'Disability level'} options={filters.disability_levels} value={form.disability_level} onChange={(v) => setForm({ ...form, disability_level: v })} testid="new-student-level" />
              <div><Label className="text-xs">{lang === 'ar' ? 'المركز / المدرسة' : 'Center / school'}</Label><Input value={form.center} onChange={(e) => setForm({ ...form, center: e.target.value })} data-testid="new-student-center" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{lang === 'ar' ? 'بريد ولي الأمر' : 'Parent email'}</Label>
                  <Input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} placeholder="parent@..." data-testid="new-student-parent-email" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">{lang === 'ar' ? 'لربط الطالب بحساب ولي الأمر' : 'Links to parent account'}</p>
                </div>
                <div>
                  <Label className="text-xs">{lang === 'ar' ? 'بريد الطالب' : 'Student email'}</Label>
                  <Input value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} placeholder="student@..." data-testid="new-student-self-email" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">{lang === 'ar' ? 'لربط الطالب بحساب طالب' : 'Links to student account'}</p>
                </div>
              </div>
              <Button onClick={submit} className="w-full bg-primary hover:bg-primary/90 font-bold h-10" data-testid="save-student-btn">
                <UserPlus className="w-4 h-4 me-2" />{lang === 'ar' ? 'حفظ الطالب' : 'Save student'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {students.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">{t('no_results')}</p>
        ) : (
          <div className="space-y-2">
            {students.map((s) => (
              <div key={s.id} className="flex items-center justify-between border border-border rounded-lg p-3" data-testid={`student-row-${s.id}`}>
                <div>
                  <div className="font-semibold">{s.full_name_ar}</div>
                  <div className="text-xs text-muted-foreground">{s.disability_type} · {s.age_group}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{s.points || 0} {t('points')}</Badge>
                  {(s.badges || []).length > 0 && <Badge className="bg-accent text-accent-foreground">{s.badges.length} {t('badges')}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function IEPPanel({ students }) {
  const { t, lang } = useApp();
  const [ieps, setIeps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: '', student_name_ar: '', disability_type: '', disability_level: '', age_group: '', center_name: '', notes_ar: '', goals: [] });

  useEffect(() => { api.get('/iep').then(r => setIeps(r.data)); }, []);

  const submit = async () => {
    try {
      const { data } = await api.post('/iep', form);
      setIeps([data, ...ieps]);
      toast.success(t('saved'));
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || t('error')); }
  };

  const onPick = (sid) => {
    const s = students.find(x => x.id === sid);
    if (s) setForm({ ...form, student_id: sid, student_name_ar: s.full_name_ar, disability_type: s.disability_type, disability_level: s.disability_level, age_group: s.age_group, center_name: s.center || '' });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('iep_builder')}</CardTitle>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="bg-primary" data-testid="new-iep-btn"><Plus className="w-4 h-4 me-1" />{t('new_iep')}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('new_iep')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('student_name')}</Label>
                <Select value={form.student_id} onValueChange={onPick}>
                  <SelectTrigger data-testid="iep-pick-student"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {students.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name_ar}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{lang === 'ar' ? 'ملاحظات' : 'Notes'}</Label><Textarea rows={3} value={form.notes_ar} onChange={(e) => setForm({ ...form, notes_ar: e.target.value })} data-testid="iep-notes" /></div>
              <Button onClick={submit} disabled={!form.student_id} className="w-full bg-primary" data-testid="save-iep-btn">{t('save')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {ieps.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">{t('no_results')}</p>
        ) : (
          <div className="space-y-2">
            {ieps.map(p => (
              <div key={p.id} className="border border-border rounded-lg p-3" data-testid={`iep-row-${p.id}`}>
                <div className="font-semibold">{p.student_name_ar}</div>
                <div className="text-xs text-muted-foreground">{p.disability_type} · {p.age_group}</div>
                {p.notes_ar && <div className="text-sm mt-2 text-muted-foreground">{p.notes_ar}</div>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlanPanel() {
  const { t, lang } = useApp();
  return (
    <Card>
      <CardHeader><CardTitle>{lang === 'ar' ? 'الخطط (سنوي/فصلي/أسبوعي)' : 'Plans (Annual / Term / Weekly)'}</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-3 gap-4">
        {[['annual','الخطة السنوية','Annual Plan'],['term','الخطة الفصلية','Term Plan'],['weekly','الخطة الأسبوعية','Weekly Plan']].map(([k, ar, en]) => (
          <div key={k} className="border border-border rounded-lg p-5" data-testid={`plan-${k}`}>
            <div className="font-bold mb-2">{lang === 'ar' ? ar : en}</div>
            <p className="text-sm text-muted-foreground">{lang === 'ar' ? 'استخدم مكتبة المنهج لاختيار أهداف وإضافتها لخططك.' : 'Use the curriculum library to pick goals and add to your plans.'}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
